/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { ApplicationState } from 'store';
import {
  FollowupsTypes,
  MarketState,
  ProductsTypes,
  ResultsTypes,
} from 'store/modules/market_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import {
  getClientFilters,
  getCompanyData,
  getFollowups,
  getProviderBiddings,
  getResults,
  productsServices,
  searchBuyers,
  searchFilters,
  searchProviders,
} from 'store/modules/market_store/actions';
import companyId from 'services/company_id';
import { States, StatesList } from 'services/states';
import {
  FiltrosAmostras,
  fmtBRL,
  fmtInt,
  PERIODO_PERSONALIZADO,
  totalFiltrosAmostras,
  VolumePoint,
} from 'services/price_intelligence';
import Pagination from 'components/atoms/pagination';
import BuyersModal from 'components/atoms/buyers_modal';
import ProvidersModal from 'components/atoms/providers_modal';
import ResultMarketModal from 'components/atoms/result_market_modal';
import ProductsModal from 'components/atoms/products_modal';
import FollowupstModal from 'components/atoms/followups_modal';
import { ChartCard, VolumeChart } from 'components/molecules/price_intelligence_charts';
import { Container as Shell } from 'components/organisms/price_intelligence_results/styles';
import { Container as Tabela } from 'components/molecules/price_intelligence_sample_table/styles';
import { Container } from './styles';

const MAX_MESES = 24;
const POR_PAGINA = 10;
const POR_PAGINA_CONCORRENTES = 8;
const PRIMEIRA_PAGINA = {
  orgaos: 1,
  concorrentes: 1,
  resultados: 1,
  produtos: 1,
  acompanhamentos: 1,
};
const SEM_SELECAO = 0;

type Modo = 'todas' | 'qualquer' | 'frase';
const MODOS: Array<{ value: Modo; label: string }> = [
  { value: 'frase', label: 'Frase exata' },
  { value: 'todas', label: 'Todas as palavras' },
  { value: 'qualquer', label: 'Qualquer palavra' },
];

interface Tentativa {
  termo: string;
  modo: Modo;
  base: boolean;
}

const unicos = (termos: string[]): string[] =>
  termos
    .map((t) => t.trim())
    .filter(
      (t, i, lista) =>
        Boolean(t) && lista.findIndex((o) => o.toLowerCase() === t.toLowerCase()) === i,
    );

// Sem licitação na Seleção: relaxa a frase, tenta as buscas da IA e, por fim, a base inteira.
const tentativasAutomaticas = (termo: string, alternativas: string[]): Tentativa[] => {
  if (!termo.trim()) return [{ termo: '', modo: 'frase', base: false }];
  const [principal, ...variantes] = unicos([termo, ...alternativas]);
  const naSelecao: Tentativa[] = [{ termo: principal, modo: 'frase', base: false }];
  if (principal.split(/\s+/).length > 1)
    naSelecao.push({ termo: principal, modo: 'todas', base: false });
  variantes.forEach((v) => naSelecao.push({ termo: v, modo: 'todas', base: false }));
  return naSelecao.concat(naSelecao.map((t) => ({ ...t, base: true })));
};

const tentativasNoModo = (termo: string, modo: Modo): Tentativa[] => {
  const t = termo.trim();
  if (!t) return [{ termo: '', modo, base: false }];
  return [
    { termo: t, modo, base: false },
    { termo: t, modo, base: true },
  ];
};

// Sem Seleção não há o que tentar dentro dela: a cadeia só passa pela base inteira.
const naBaseInteira = (tentativas: Tentativa[]): Tentativa[] => {
  const naBase = tentativas.filter((t) => t.base);
  return naBase.length ? naBase : tentativas.map((t) => ({ ...t, base: true }));
};

const CRITERIOS: Record<Modo, string> = {
  frase: 'a frase exata',
  todas: 'todas as palavras de',
  qualquer: 'alguma das palavras de',
};

const REGIOES_MERCADO = [
  { chave: 'sudeste', nome: 'Sudeste' },
  { chave: 'sul', nome: 'Sul' },
  { chave: 'nordeste', nome: 'Nordeste' },
  { chave: 'centro_oeste', nome: 'Centro-Oeste' },
  { chave: 'norte', nome: 'Norte' },
];

// O índice só aceita "últimos N meses": um período personalizado vira a distância até hoje.
const mesesDoPeriodo = (periodoMeses: number, dataDe: string): number => {
  if (periodoMeses !== PERIODO_PERSONALIZADO) return Math.min(periodoMeses, MAX_MESES);
  if (!dataDe) return 12;
  const inicio = new Date(`${dataDe}T00:00:00`);
  const hoje = new Date();
  const meses =
    (hoje.getFullYear() - inicio.getFullYear()) * 12 + hoje.getMonth() - inicio.getMonth() + 1;
  return Math.max(1, Math.min(meses, MAX_MESES));
};

const idsDosEstados = (ufs: string[]): string =>
  States.filter((s) => ufs.indexOf(s.uf) >= 0)
    .map((s) => s.value)
    .join(',');

// A Seleção pode restringir UFs; o backend aplica isso sozinho, aqui só mostramos.
const ufsDaSelecao = (bidding: any): { negado: boolean; siglas: string[] } | null => {
  const opcao = Array.isArray(bidding)
    ? bidding.find((o: any) => o.field === 'orgao_estado_id')
    : null;
  if (!opcao || !Array.isArray(opcao.values) || !opcao.values.length) return null;
  const siglas = opcao.values.map((nome: string) => {
    const estado = StatesList.find((e) => e.name === nome);
    return estado ? estado.acronym : nome;
  });
  return { negado: Boolean(opcao.is_negation), siglas };
};

const atividadesDaSelecao = (bidding: any): string[] => {
  const opcao = Array.isArray(bidding) ? bidding.find((o: any) => o.field === 'activity_id') : null;
  return opcao && Array.isArray(opcao.values) ? opcao.values : [];
};

const ROTULOS_REFINO: Array<{ campo: keyof FiltrosAmostras; nome: string }> = [
  { campo: 'descricao', nome: 'descrição' },
  { campo: 'orgao', nome: 'órgão' },
  { campo: 'municipio', nome: 'cidade' },
  { campo: 'fornecedor', nome: 'concorrente' },
  { campo: 'marca', nome: 'marca' },
];

const resumoRefino = (f: FiltrosAmostras): string[] => {
  const partes = ROTULOS_REFINO.filter((r) => f[r.campo].trim()).map(
    (r) => `${r.nome} “${f[r.campo].trim()}”`,
  );
  const min = f.valorMin.trim();
  const max = f.valorMax.trim();
  if (min && max) partes.push(`valor unitário de ${fmtBRL(Number(min))} a ${fmtBRL(Number(max))}`);
  else if (min) partes.push(`valor unitário a partir de ${fmtBRL(Number(min))}`);
  else if (max) partes.push(`valor unitário até ${fmtBRL(Number(max))}`);
  return partes;
};

// Os buckets vêm em ordem crescente e terminam no mês anterior ao atual.
const serieMensal = (valores: Array<{ y: number }>): VolumePoint[] => {
  const hoje = new Date();
  const n = valores.length;
  return valores.map((v, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - (n - i), 1);
    const mes = String(d.getMonth() + 1);
    return { month: `${d.getFullYear()}-${mes.length === 1 ? `0${mes}` : mes}`, n: v.y };
  });
};

const investimento = (
  resultados: Array<{ item_valor_total: number; item_valor_unitario: number }>,
) => {
  const total = resultados.reduce((acc, r) => acc + (r.item_valor_total || 0), 0);
  if (total > 0) return total;
  return resultados.reduce((acc, r) => acc + (r.item_valor_unitario || 0), 0);
};

// Os endpoints do Mercado param de contar em 10.000.
const TETO = 10000;
const comTeto = (n: number) => (n >= TETO ? `${fmtInt(TETO)}+` : fmtInt(n));

const cortar = (texto: string | null | undefined, max: number) =>
  texto && texto.length > max ? `${texto.substring(0, max)}…` : texto || '';

interface Pedido {
  n: number;
  criar: () => any;
  aoConcluir?: (resposta: any) => void;
  concluido: boolean;
}

const Carregando = () => (
  <span className="carregando">
    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
    Carregando…
  </span>
);

const ShareRow = ({
  nome,
  n,
  total,
  larga,
}: {
  nome: string;
  n: number;
  total: number;
  larga: boolean;
}) => {
  const pct = total ? (n * 100) / total : 0;
  return (
    <div className={`region-row${larga ? ' larga' : ''}`}>
      <span className="region-name" title={nome}>
        {nome}
      </span>
      <div className="region-track">
        <div className="region-bar" style={{ width: `${Math.max(pct, n > 0 ? 1 : 0)}%` }} />
      </div>
      <span className="region-value">
        {fmtInt(n)}
        <small>{Math.round(pct)}%</small>
      </span>
    </div>
  );
};

interface PriceIntelligenceMarketProps {
  termo: string;
  alternativas: string[];
  refino: FiltrosAmostras;
  periodoMeses: number;
  dataDe: string;
  ufs: string[];
}

const PriceIntelligenceMarket = ({
  termo,
  alternativas,
  refino,
  periodoMeses,
  dataDe,
  ufs,
}: PriceIntelligenceMarketProps) => {
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((s) => s.storage.profile);
  const {
    market,
    get_filters,
    orgao,
    concorrentes,
    resultado,
    products_services,
    bidding,
    request,
  } = useSelector<ApplicationState, MarketState>((s) => s.session.market);

  const [filtroId, setFiltroId] = useState<number>(SEM_SELECAO);
  const [palavra, setPalavra] = useState<string>(termo);
  const [tentativasDaBusca, setTentativas] = useState<Tentativa[]>(() =>
    tentativasAutomaticas(termo, alternativas),
  );
  const [indice, setIndice] = useState<number>(0);
  const [refinoAplicado, setRefinoAplicado] = useState<FiltrosAmostras>(refino);
  const [params, setParams] = useState<any>(null);
  const [pagina, setPagina] = useState(PRIMEIRA_PAGINA);
  const [orgaoAberto, setOrgaoAberto] = useState<number>(0);
  const [orgaoModal, setOrgaoModal] = useState<boolean>(false);
  const [fichaCnpj, setFichaCnpj] = useState<string>('');
  const [fichaAberta, setFichaAberta] = useState<boolean>(false);
  const [paginaFicha, setPaginaFicha] = useState<number>(1);
  const [resultadoAberto, setResultadoAberto] = useState<ResultsTypes | null>(null);
  const [produtoAberto, setProdutoAberto] = useState<ProductsTypes | null>(null);
  const [acompAberto, setAcompAberto] = useState<FollowupsTypes | null>(null);
  const impressaoRef = useRef<HTMLDivElement>(null);
  const pedidos = useRef<Record<string, Pedido>>({});
  const contextoRef = useRef<string>('');

  // Uma resposta antiga pode chegar depois da mais nova e sobrescrever o store; se isso
  // acontecer, refaz a mais nova.
  const pedir = (chave: string, criar: () => any, aoConcluir?: (resposta: any) => void) => {
    const anterior = pedidos.current[chave];
    const atual: Pedido = { n: anterior ? anterior.n + 1 : 1, criar, aoConcluir, concluido: false };
    pedidos.current[chave] = atual;
    const promessa = dispatch(criar()) as unknown as Promise<any>;
    promessa.then(
      (resposta) => {
        const ultimo = pedidos.current[chave];
        if (ultimo === atual) {
          atual.concluido = true;
          if (aoConcluir) aoConcluir(resposta);
        } else if (ultimo.concluido) pedir(chave, ultimo.criar, ultimo.aoConcluir);
      },
      () => {},
    );
  };

  const empresa = companyId(profile);
  const intervalo = mesesDoPeriodo(periodoMeses, dataDe);
  const estados = idsDosEstados(ufs);
  const chaveRefino = JSON.stringify(refino);
  const chaveAplicada = JSON.stringify(refinoAplicado);
  const chaveAlternativas = JSON.stringify(alternativas);
  const semSelecao = filtroId === SEM_SELECAO;
  const tentativas = semSelecao ? naBaseInteira(tentativasDaBusca) : tentativasDaBusca;
  const primeira = tentativas[0];
  const atual = tentativas[indice] || primeira;
  const criterio = CRITERIOS[atual.modo];
  const chaveContexto = JSON.stringify([filtroId, intervalo, estados, chaveAplicada, tentativas]);

  useEffect(() => {
    dispatch(getClientFilters(empresa));
  }, [empresa]);

  useEffect(() => {
    if (!request.get_filters.loaded || semSelecao) return;
    const existe = get_filters.some((f) => f.filter.id === filtroId);
    if (!existe) setFiltroId(SEM_SELECAO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.get_filters.loaded, get_filters]);

  useEffect(() => {
    setPalavra(termo);
    setTentativas(tentativasAutomaticas(termo, alternativas));
    setIndice(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, chaveAlternativas]);

  // Os filtros avançados chegam a cada tecla; espera o usuário parar de digitar.
  useEffect(() => {
    const espera = setTimeout(() => setRefinoAplicado(refino), 600);
    return () => clearTimeout(espera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveRefino]);

  useEffect(() => {
    // Mudou a busca, a Seleção ou um filtro: a cadeia de tentativas recomeça.
    if (contextoRef.current !== chaveContexto) {
      contextoRef.current = chaveContexto;
      if (indice !== 0) {
        setIndice(0);
        return;
      }
    }
    const r = refinoAplicado;
    const temValor = Boolean(r.valorMin.trim() || r.valorMax.trim());
    const novos = {
      filter_id: semSelecao ? undefined : filtroId,
      interval: intervalo,
      term: atual.termo,
      exact_search: atual.modo === 'frase',
      term_mode: atual.modo === 'qualquer' ? '' : 'all',
      scope: atual.base ? 'all' : '',
      advanced_search: Boolean(estados) || atual.base,
      states: estados,
      cities: '',
      modalities: '',
      public_body_types: '',
      refine_item: r.descricao.trim(),
      refine_brand: r.marca.trim(),
      refine_buyer: r.orgao.trim(),
      refine_city: r.municipio.trim(),
      refine_provider: r.fornecedor.trim(),
      min_value: temValor ? r.valorMin.trim() || '0' : '',
      max_value: temValor ? r.valorMax.trim() || '999999999' : '',
      type_value: '0',
    };
    // Órgãos, concorrentes, resultados, produtos e acompanhamentos só saem quando a cadeia para.
    setParams(null);
    setPagina(PRIMEIRA_PAGINA);
    const proximo = indice + 1;
    pedir(
      'search',
      () => searchFilters(novos),
      (resposta) => {
        if (contextoRef.current !== chaveContexto) return;
        const total = resposta && resposta.data ? resposta.data.total_entries : null;
        if (total === 0 && proximo < tentativas.length) setIndice(proximo);
        else setParams(novos);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveContexto, indice]);

  useEffect(() => {
    if (params) pedir('orgaos', () => searchBuyers(params, pagina.orgaos));
  }, [params, pagina.orgaos]);

  useEffect(() => {
    if (params) pedir('concorrentes', () => searchProviders(params, pagina.concorrentes));
  }, [params, pagina.concorrentes]);

  useEffect(() => {
    if (params) pedir('resultados', () => getResults(params, pagina.resultados));
  }, [params, pagina.resultados]);

  useEffect(() => {
    if (params) pedir('produtos', () => productsServices(params, pagina.produtos));
  }, [params, pagina.produtos]);

  useEffect(() => {
    if (params) pedir('acompanhamentos', () => getFollowups(params, pagina.acompanhamentos));
  }, [params, pagina.acompanhamentos]);

  useEffect(() => {
    if (fichaCnpj) dispatch(getProviderBiddings(fichaCnpj, paginaFicha));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaFicha]);

  const imprimir = useReactToPrint({
    content: () => impressaoRef.current,
    documentTitle: 'panorama_de_mercado',
  });

  const abrirOrgao = (id: number) => {
    setOrgaoAberto(id);
    setOrgaoModal(true);
  };

  const abrirFicha = (cnpj: string) => {
    dispatch(getCompanyData(cnpj));
    setPaginaFicha(1);
    setFichaCnpj(cnpj);
    dispatch(getProviderBiddings(cnpj, 1));
    setFichaAberta(true);
  };

  const mudarPagina = (chave: keyof typeof PRIMEIRA_PAGINA) => (nova: number) =>
    setPagina((atual) => ({ ...atual, [chave]: nova }));

  const opcoesSelecao = [
    { value: SEM_SELECAO, label: 'Sem Seleção (base inteira)' },
    ...get_filters.map((f) => ({
      value: f.filter.id,
      label: f.filter.description,
    })),
  ];
  const selecao = opcoesSelecao.find((o) => o.value === filtroId) || null;
  const filtroAtual = get_filters.find((f) => f.filter.id === filtroId);
  const ufsSelecao = filtroAtual ? ufsDaSelecao(filtroAtual.bidding) : null;
  const atividades = filtroAtual ? atividadesDaSelecao(filtroAtual.bidding) : [];
  const refinando = totalFiltrosAmostras(refinoAplicado) > 0;
  const regioes = market && market.estados ? market.estados.regions[0] : null;
  const modalidades: Array<{ modalidade: string; total: number }> = market
    ? (market.modalities as any)
    : [];
  const total = market ? market.total_entries : 0;
  const resolvendo = !params;
  const carregandoTotais = resolvendo || request.search.loading;
  const volume = market && market.date_range ? serieMensal(market.date_range) : [];
  const orgaos = orgao && orgao.results ? orgao.results : [];
  const fornecedores = concorrentes && concorrentes.results ? concorrentes.results : [];
  const resultados = resultado && resultado.resultados ? resultado.resultados : [];
  const produtos =
    products_services && products_services.products ? products_services.products : [];
  const acompanhamentos = bidding && bidding.licitacao ? bidding.licitacao : [];

  const descricaoDoPeriodo = `últimos ${intervalo} meses`;
  let abrangencia = 'Brasil inteiro';
  if (ufs.length > 0) abrangencia = ufs.join(', ');
  else if (ufsSelecao && !atual.base)
    abrangencia = `${ufsSelecao.negado ? 'exceto ' : ''}${ufsSelecao.siglas.join(
      ', ',
    )} (UFs da Seleção)`;

  const daSelecao = atual.base ? '' : ' da Seleção';
  const esgotou =
    !resolvendo && total === 0 && tentativas.length > 1 && indice === tentativas.length - 1;
  const palavraExibida = esgotou ? primeira.termo : atual.termo;
  let aviso: string | null = null;
  if (!resolvendo && indice > 0 && primeira.termo) {
    const variante = atual.termo !== primeira.termo;
    const naSelecao = semSelecao ? '' : ' da Seleção';
    if (esgotou)
      aviso = `Nenhuma licitação com “${primeira.termo}” nos ${descricaoDoPeriodo}${
        semSelecao ? ' na' : ', nem na Seleção nem na'
      } base inteira da ConLicitação.`;
    else if (semSelecao || !atual.base)
      aviso = variante
        ? `Nenhuma licitação${naSelecao} com “${primeira.termo}”; o panorama usa a variante “${atual.termo}”, uma das buscas feitas pela IA.`
        : `Nenhuma licitação${naSelecao} com a frase exata “${primeira.termo}”; o panorama mostra as que têm todas as palavras.`;
    else {
      let complemento = '';
      if (variante) complemento = `, com a variante “${atual.termo}” (busca feita pela IA)`;
      else if (atual.modo !== primeira.modo) complemento = ', com todas as palavras';
      aviso = `A Seleção${selecao ? ` “${selecao.label}”` : ''} não tem licitações com “${
        primeira.termo
      }” nos ${descricaoDoPeriodo}; o panorama mostra a base inteira da ConLicitação, de todas as atividades${complemento}.`;
    }
  }

  const escolher = (texto: string, modo: Modo | null) => {
    setTentativas(modo ? tentativasNoModo(texto, modo) : tentativasAutomaticas(texto, []));
    setIndice(0);
  };

  return (
    <Container ref={impressaoRef}>
      <Shell className="shell">
        <div className="results-head">
          <div>
            <p className="results-title">
              Panorama de mercado
              {palavraExibida ? ` para “${palavraExibida}”` : ''}
            </p>
            <p className="results-sub">
              {atual.base
                ? 'Licitações de todas as atividades'
                : `Licitações da Seleção ${selecao ? `“${selecao.label}”` : ''}`}{' '}
              nos {descricaoDoPeriodo}
              {` · ${abrangencia}`}
              {!atual.base && atividades.length > 0 && (
                <span title={atividades.join(' · ')}>
                  {` · ${atividades.length} ${
                    atividades.length === 1 ? 'atividade' : 'atividades'
                  } da Seleção`}
                </span>
              )}
              {' · base de licitações da ConLicitação'}
            </p>
          </div>
          <div className="results-actions d-print-none">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={imprimir}>
              <i className="material-icons">print</i>
              Imprimir
            </button>
          </div>
        </div>

        <div className="market-filters d-print-none">
          <span className="filter-label">
            <i className="material-icons">filter_list</i>
            Seleção
          </span>
          <div className="selecao">
            <Select
              classNamePrefix="pi-select"
              options={opcoesSelecao}
              value={selecao}
              isLoading={request.get_filters.loading}
              placeholder="Selecione"
              noOptionsMessage={() => 'Nenhuma Seleção'}
              onChange={(opcao: any) => setFiltroId(opcao ? opcao.value : SEM_SELECAO)}
            />
          </div>
          <span className="filter-label">
            <i className="material-icons">search</i>
            Palavra-chave
          </span>
          <form
            className="palavra input-group input-group-sm"
            onSubmit={(e) => {
              e.preventDefault();
              escolher(palavra, null);
            }}
          >
            <input
              type="text"
              className="form-control"
              value={palavra}
              onChange={(e) => setPalavra(e.target.value)}
              placeholder="Refinar o panorama por palavra-chave"
              aria-label="Palavra-chave do panorama"
            />
            <div className="input-group-append">
              <button type="submit" className="btn btn-outline-secondary">
                Aplicar
              </button>
            </div>
          </form>
          <select
            className="form-control form-control-sm modo"
            value={atual.modo}
            aria-label="Como combinar as palavras"
            onChange={(e) => escolher(palavra, e.target.value as Modo)}
          >
            {MODOS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {aviso && (
          <p className="market-refino">
            <i className="material-icons">info</i>
            <span>{aviso}</span>
          </p>
        )}

        {refinando && (
          <p className="market-refino">
            <i className="material-icons">tune</i>
            <span>
              Filtros avançados aplicados: {resumoRefino(refinoAplicado).join(' · ')}
              {refinoAplicado.municipio.trim()
                ? ' (a cidade não se aplica a Produtos e serviços)'
                : ''}
            </span>
          </p>
        )}

        {request.search.error ? (
          <div className="market-empty">Não foi possível carregar o panorama agora.</div>
        ) : (
          <>
            <div className="stat-tiles">
              <div className="stat-tile accent">
                <p className="stat-label">Compras públicas no período</p>
                <p className="stat-value">{carregandoTotais ? '…' : fmtInt(total)}</p>
                <p className="stat-hint">
                  licitações{daSelecao} nos {descricaoDoPeriodo}
                </p>
              </div>
              <div className="stat-tile">
                <p className="stat-label">Com resultado homologado</p>
                <p className="stat-value">
                  {resolvendo || request.get_results.loading
                    ? '…'
                    : fmtInt(resultado ? resultado.total || 0 : 0)}
                </p>
                <p className="stat-hint">licitações com vencedores conhecidos</p>
              </div>
              <div className="stat-tile">
                <p className="stat-label">Órgãos compradores</p>
                <p className="stat-value">
                  {resolvendo || request.search_buyers.loading
                    ? '…'
                    : comTeto(orgao ? orgao.total || 0 : 0)}
                </p>
                <p className="stat-hint">órgãos distintos com licitações</p>
              </div>
              <div className="stat-tile">
                <p className="stat-label">Com acompanhamentos</p>
                <p className="stat-value">
                  {resolvendo || request.get_followups.loading
                    ? '…'
                    : comTeto(bidding ? bidding.total || 0 : 0)}
                </p>
                <p className="stat-hint">licitações com movimentação registrada</p>
              </div>
            </div>

            <div className="charts-grid">
              <div className="wide">
                <ChartCard
                  title="Licitações por mês"
                  subtitle={`Publicações${daSelecao} ao longo do período`}
                >
                  {carregandoTotais ? (
                    <Carregando />
                  ) : (
                    <VolumeChart
                      points={volume}
                      rotulo={{ um: 'licitação', varios: 'licitações' }}
                      barraMax={64}
                    />
                  )}
                </ChartCard>
              </div>
              <ChartCard
                title="Licitações por região"
                subtitle="Participação de cada região no total"
              >
                {carregandoTotais ? (
                  <Carregando />
                ) : (
                  regioes &&
                  REGIOES_MERCADO.map((r) => (
                    <ShareRow
                      key={r.chave}
                      nome={r.nome}
                      n={regioes[r.chave].licitacao}
                      total={total}
                      larga={false}
                    />
                  ))
                )}
              </ChartCard>
              <ChartCard title="Licitações por modalidade" subtitle="Como as compras são feitas">
                {carregandoTotais ? (
                  <Carregando />
                ) : modalidades.length ? (
                  modalidades.map((m) => (
                    <ShareRow
                      key={m.modalidade}
                      nome={m.modalidade}
                      n={m.total}
                      total={total}
                      larga
                    />
                  ))
                ) : (
                  <p className="chart-empty">Nenhuma licitação no período.</p>
                )}
              </ChartCard>
            </div>

            <div className="competitive-grid">
              <div className="competitive-card">
                <p className="comp-title">
                  <i className="material-icons">account_balance</i>
                  Órgãos que mais compram
                </p>
                <p className="comp-sub">
                  Por número de licitações no período · clique para ver a ficha
                </p>
                {resolvendo || request.search_buyers.loading ? (
                  <Carregando />
                ) : orgaos.length ? (
                  <ul>
                    {orgaos.map((o, idx) => (
                      <li key={o.orgao_id}>
                        <span className="comp-rank">
                          {(pagina.orgaos - 1) * POR_PAGINA + idx + 1}
                        </span>
                        <button
                          type="button"
                          className="comp-name comp-link"
                          onClick={() => abrirOrgao(o.orgao_id)}
                          title={o.nome}
                        >
                          {o.nome}
                          <span className="comp-place">
                            {' '}
                            · {o.cidade ? `${o.cidade}/` : ''}
                            {o.uf}
                          </span>
                        </button>
                        <span className="comp-metric">
                          <div className="metric-main">{fmtInt(o.doc_count)}</div>
                          <div className="metric-sub">
                            {o.doc_count === 1 ? 'licitação' : 'licitações'}
                          </div>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="comp-empty">Nenhum órgão no período.</p>
                )}
                {orgao && orgao.total > POR_PAGINA && (
                  <div className="comp-more d-print-none">
                    <Pagination
                      postsPerPage={POR_PAGINA}
                      totalPosts={orgao.total}
                      paginate={mudarPagina('orgaos')}
                      currentPage={pagina.orgaos}
                      firstAndLastPage
                    />
                  </div>
                )}
              </div>
              <div className="competitive-card">
                <p className="comp-title">
                  <i className="material-icons">storefront</i>
                  Concorrentes relacionados
                </p>
                <p className="comp-sub">
                  Vencedores de resultados no período · clique para ver a ficha
                </p>
                {resolvendo || request.search_providers.loading ? (
                  <Carregando />
                ) : fornecedores.length ? (
                  <ul>
                    {fornecedores.map((c, idx) => (
                      <li key={c.cnpj}>
                        <span className="comp-rank">
                          {(pagina.concorrentes - 1) * POR_PAGINA_CONCORRENTES + idx + 1}
                        </span>
                        <button
                          type="button"
                          className="comp-name comp-link"
                          onClick={() => abrirFicha(String(c.cnpj))}
                          title={c.razao_social}
                        >
                          {c.razao_social}
                        </button>
                        <span className="comp-metric">
                          <div className="metric-main">{fmtInt(c.total_results)}</div>
                          <div className="metric-sub">
                            {c.total_results === 1 ? 'vitória' : 'vitórias'}
                            {c.valor_total > 0 ? ` · ${fmtBRL(c.valor_total)}` : ''}
                          </div>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="comp-empty">Nenhum concorrente com resultado no período.</p>
                )}
                {concorrentes && concorrentes.total > POR_PAGINA_CONCORRENTES && (
                  <div className="comp-more d-print-none">
                    <Pagination
                      postsPerPage={POR_PAGINA_CONCORRENTES}
                      totalPosts={concorrentes.total}
                      paginate={mudarPagina('concorrentes')}
                      currentPage={pagina.concorrentes}
                      firstAndLastPage
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="samples-card">
              <div className="samples-head">
                <div>
                  <p className="samples-title">
                    Resultados homologados
                    {resultado && resultado.total ? ` (${fmtInt(resultado.total)})` : ''}
                  </p>
                  <p className="samples-sub">
                    Licitações{daSelecao} com vencedores conhecidos · clique para ver os itens
                  </p>
                </div>
              </div>
              {resolvendo || request.get_results.loading ? (
                <div className="px-3">
                  <Carregando />
                </div>
              ) : (
                <Tabela>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Edital</th>
                        <th>Órgão</th>
                        <th>Vencedores</th>
                        <th className="text-center">Itens</th>
                        <th>Abertura</th>
                        <th className="text-right">Investimento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.map((r) => (
                        <tr
                          key={r.licitacao.id}
                          className="selectable"
                          onClick={() => setResultadoAberto(r)}
                        >
                          <td className="date-cell">{r.licitacao.edital}</td>
                          <td className="org-cell">
                            <div className="org-name">
                              {r.licitacao.orgao && r.licitacao.orgao.nome}
                            </div>
                          </td>
                          <td className="supplier-cell">
                            <div className="supplier-plain">
                              {r.licitacao.resultados[0] && r.licitacao.resultados[0].fornecedor}
                            </div>
                            {r.licitacao.resultados.length > 1 && (
                              <span className="org-place">
                                e mais {r.licitacao.resultados.length - 1}
                              </span>
                            )}
                          </td>
                          <td className="num-cell">{r.licitacao.resultados.length}</td>
                          <td className="date-cell">
                            {moment.parseZone(r.licitacao.data_validade).format('L')}
                          </td>
                          <td className="value-cell">
                            <span className="value-main">
                              {fmtBRL(investimento(r.licitacao.resultados))}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {!resultados.length && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            Nenhum resultado no período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Tabela>
              )}
              {resultado && resultado.total > POR_PAGINA && (
                <div className="samples-footer d-print-none">
                  <Pagination
                    postsPerPage={POR_PAGINA}
                    totalPosts={resultado.total}
                    paginate={mudarPagina('resultados')}
                    currentPage={pagina.resultados}
                    firstAndLastPage
                  />
                </div>
              )}
            </div>

            <div className="samples-card">
              <div className="samples-head">
                <div>
                  <p className="samples-title">
                    Produtos e serviços homologados
                    {products_services && typeof products_services.total === 'number'
                      ? ` (${fmtInt(products_services.total)})`
                      : ''}
                  </p>
                  <p className="samples-sub">
                    {atual.base
                      ? 'Itens vencidos em todas as atividades'
                      : 'Itens vencidos nas atividades da Seleção'}{' '}
                    · clique para ver o detalhe
                  </p>
                </div>
              </div>
              {resolvendo || request.get_products.loading ? (
                <div className="px-3">
                  <Carregando />
                </div>
              ) : (
                <Tabela>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Descrição</th>
                        <th>Vencedor</th>
                        <th className="text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {produtos.map((p) => {
                        const unitario =
                          Number(p.item_valor_unitario) > 0 ? Number(p.item_valor_unitario) : 0;
                        const totalItem =
                          Number(p.item_valor_total) > 0 ? Number(p.item_valor_total) : 0;
                        return (
                          <tr key={p.id} className="selectable" onClick={() => setProdutoAberto(p)}>
                            <td className="desc-cell">
                              <div className="desc-main">{p.item_descricao}</div>
                            </td>
                            <td className="sintese-cell">
                              <div className="sintese-text">
                                {cortar(p.item_descricao_detalhado || p.objeto, 360)}
                              </div>
                            </td>
                            <td className="supplier-cell">
                              <div className="supplier-plain">{p.fornecedor}</div>
                            </td>
                            <td className="value-cell">
                              <span className="value-main">
                                {fmtBRL(unitario > 0 ? unitario : totalItem)}
                              </span>
                              <span className="value-normalized">
                                {unitario > 0 ? 'unitário' : 'total'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {!produtos.length && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            {atual.termo
                              ? `Nenhum item homologado com ${criterio} “${atual.termo}” ${
                                  atual.base ? 'na base inteira' : 'nas atividades da Seleção'
                                }.`
                              : 'Nenhum item homologado no período.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Tabela>
              )}
              {products_services && products_services.total > POR_PAGINA && (
                <div className="samples-footer d-print-none">
                  <Pagination
                    postsPerPage={POR_PAGINA}
                    totalPosts={products_services.total}
                    paginate={mudarPagina('produtos')}
                    currentPage={pagina.produtos}
                    firstAndLastPage
                  />
                </div>
              )}
            </div>

            <div className="samples-card">
              <div className="samples-head">
                <div>
                  <p className="samples-title">
                    Acompanhamentos
                    {bidding && bidding.total ? ` (${fmtInt(bidding.total)})` : ''}
                  </p>
                  <p className="samples-sub">
                    Movimentações das licitações{daSelecao} · clique para ler a síntese completa
                  </p>
                </div>
              </div>
              {resolvendo || request.get_followups.loading ? (
                <div className="px-3">
                  <Carregando />
                </div>
              ) : (
                <Tabela>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Edital</th>
                        <th>Órgão</th>
                        <th>Síntese</th>
                        <th className="text-center">Qtd.</th>
                        <th>Data da fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acompanhamentos.map((a) => (
                        <tr
                          key={a.licitacao.id}
                          className="selectable"
                          onClick={() => setAcompAberto(a)}
                        >
                          <td className="date-cell">{a.licitacao.edital}</td>
                          <td className="org-cell">
                            <div className="org-name">{a.licitacao.orgao.nome}</div>
                            <div className="org-place">
                              {a.licitacao.orgao.cidade}/{a.licitacao.orgao.estado}
                            </div>
                          </td>
                          <td className="sintese-cell">
                            <div className="sintese-text">
                              {a.licitacao.acompanhamentos[0] &&
                                a.licitacao.acompanhamentos[0].sintese}
                            </div>
                          </td>
                          <td className="num-cell">{a.licitacao.acompanhamentos.length}</td>
                          <td className="date-cell">
                            {a.licitacao.acompanhamentos[0] &&
                              moment
                                .parseZone(a.licitacao.acompanhamentos[0].data_fonte)
                                .format('L')}
                          </td>
                        </tr>
                      ))}
                      {!acompanhamentos.length && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            Nenhum acompanhamento no período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Tabela>
              )}
              {bidding && bidding.total > POR_PAGINA && (
                <div className="samples-footer d-print-none">
                  <Pagination
                    postsPerPage={POR_PAGINA}
                    totalPosts={bidding.total}
                    paginate={mudarPagina('acompanhamentos')}
                    currentPage={pagina.acompanhamentos}
                    firstAndLastPage
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Shell>

      <BuyersModal
        showModal={orgaoModal}
        setShowModal={setOrgaoModal}
        orgao={orgaoAberto}
        params={params}
      />
      <ProvidersModal
        showModal={fichaAberta}
        setShowModal={setFichaAberta}
        setCurrentPageBidding={setPaginaFicha}
        currentPageBidding={paginaFicha}
      />
      {resultadoAberto && (
        <ResultMarketModal
          showModal={Boolean(resultadoAberto)}
          setShowModal={() => setResultadoAberto(null)}
          resultData={resultadoAberto}
        />
      )}
      {produtoAberto && (
        <ProductsModal
          showModal={Boolean(produtoAberto)}
          setShowModal={() => setProdutoAberto(null)}
          products={produtoAberto}
        />
      )}
      {acompAberto && (
        <FollowupstModal
          showModal={Boolean(acompAberto)}
          setShowModal={() => setAcompAberto(null)}
          followups={acompAberto}
        />
      )}
    </Container>
  );
};

export default PriceIntelligenceMarket;
